import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronLeft, ChevronRight, ExternalLink, Mic, MicOff, Minus, Navigation, Plus, RotateCcw, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { notesToSteps, parseKitchenCommand, plainText, type NowContext } from '@tempo/core';
import { api } from '../../api';
import { duration, time } from '../../lib/format';
import { useNow } from '../../lib/hooks';
import { alarm, store, unlockAudio, useWakeLock } from '../../lib/device';
import { useSpeech } from '../../lib/speech';
import { toast } from '../../lib/toast';
import { JourneySteps } from '../NextUpCard';
import { Button } from '../ui';
import { CheckList, Ring, mmss } from './common';
import { useLectureContext } from './LectureTools';

// ───────────────────────────── study ─────────────────────────────

/** What to work on, a countdown to the end of the block, and done / need more time. */
export function StudyFocus({ n, now }: { n: NowContext; now: number }) {
  const item = n.item!;
  const qc = useQueryClient();
  const study = n.study;
  const [moreOpen, setMoreOpen] = useState(false);
  const total = item.end - item.start;
  const left = Math.max(0, item.end - now);
  const done = useMutation({
    mutationFn: () => api.post(`/blocks/${encodeURIComponent(item.id)}/status`, { status: 'done' }),
    onSuccess: () => {
      toast(n.task?.externalProgress ? 'Logged – School Manager will credit it' : 'Nice – logged');
      qc.invalidateQueries();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const more = useMutation({
    mutationFn: (minutes: number) => api.post<{ message: string }>('/assistant/more-time', { blockId: item.id, minutes }),
    onSuccess: (r) => {
      toast(r.message);
      setMoreOpen(false);
      qc.invalidateQueries();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: 22, alignItems: 'center' }}>
        <Ring fraction={total ? left / total : 0} size={150}>
          <div className="ring-time">{mmss(left)}</div>
          <div className="tiny faint">left</div>
        </Ring>
        <div className="grow stack tight">
          {(study?.module ?? n.task?.module) && <span className="badge accent" style={{ alignSelf: 'flex-start' }}>{study?.module ?? n.task?.module}</span>}
          {(study?.title ?? n.task?.title ?? item.title) !== item.title && <div className="a-big">{study?.title ?? n.task?.title}</div>}
          {n.task && <div className="small subtle">{duration(n.task.remainingMin * 60_000)} left on this task overall</div>}
        </div>
      </div>
      {study?.steps.length ? <CheckList id={`steps:${item.id}`} items={study.steps} /> : null}
      {study?.notes && <p className="small subtle" style={{ whiteSpace: 'pre-wrap' }}>{study.notes}</p>}
      {study?.materials.length ? (
        <div className="row wrap" style={{ gap: 6 }}>
          {study.materials.map((m) =>
            m.openUrl ? (
              <a key={m.label} className="btn sm" href={m.openUrl} target="_blank" rel="noreferrer">
                <ExternalLink size={14} /> {m.label}
              </a>
            ) : (
              <span key={m.label} className="badge">
                {m.label}
              </span>
            ),
          )}
        </div>
      ) : null}
      <div className="row wrap" style={{ gap: 8 }}>
        <Button variant="primary" loading={done.isPending} onClick={() => done.mutate()}>
          <Check size={16} /> Done
        </Button>
        {n.task && (
          <Button onClick={() => setMoreOpen(!moreOpen)}>
            <Plus size={16} /> Need more time
          </Button>
        )}
        {moreOpen &&
          [15, 30, 60].map((m) => (
            <Button key={m} size="sm" loading={more.isPending && more.variables === m} onClick={() => more.mutate(m)}>
              +{m} min
            </Button>
          ))}
      </div>
      {moreOpen && n.task?.externalProgress && <p className="tiny faint">This session is logged; School Manager decides how much is left and Tempo plans it.</p>}
    </div>
  );
}

/** Definitions of the concepts this study session builds on. */
export function StudyConcepts({ n }: { n: NowContext }) {
  const ctx = useLectureContext(n.study?.module ?? n.task?.module ?? null);
  const wanted = new Set((n.study?.relatedConcepts ?? []).map((c) => c.toLowerCase()));
  const entries = (ctx.data?.glossary ?? []).filter((g) => wanted.has(g.term.toLowerCase()) || g.aliases.some((a) => wanted.has(a.toLowerCase())));
  if (!entries.length) return <p className="subtle">No linked concepts for this session.</p>;
  return (
    <div className="terms">
      {entries.map((g) => (
        <div key={g.term} className="term">
          <b>{g.term}</b>
          <div className="small subtle">{plainText(g.definition)}</div>
          {g.formula && g.formula !== g.definition && <code className="small">{plainText(g.formula)}</code>}
        </div>
      ))}
    </div>
  );
}

// ───────────────────────────── travel ─────────────────────────────

export function Journey({ n, now }: { n: NowContext; now: number }) {
  const travel = n.item!;
  const dest = n.nextUp.item;
  const leaveAt = n.nextUp.journey?.leaveAt ?? n.nextUp.live?.leaveAt ?? travel.start;
  const arriveAt = n.nextUp.journey?.arriveAt ?? travel.end;
  const leftToLeave = leaveAt - now;
  return (
    <div className="stack">
      <div className="a-countdown">{leftToLeave > 60_000 ? `Leave in ${duration(leftToLeave)}` : now < arriveAt ? `Arrive ${time(arriveAt)}` : 'Arriving'}</div>
      {dest && (
        <div className="stack tight">
          <div className="a-big">{dest.title}</div>
          <div className="subtle">
            {time(dest.start)}
            {dest.location ? ` · ${dest.location}` : ''}
          </div>
        </div>
      )}
      {n.nextUp.journey && n.nextUp.journey.steps.length > 0 && <JourneySteps journey={n.nextUp.journey} />}
      {n.nextUp.directionsUrl && (
        <a className="btn primary" style={{ alignSelf: 'flex-start' }} href={n.nextUp.directionsUrl} target="_blank" rel="noreferrer">
          <Navigation size={16} /> Directions
        </a>
      )}
    </div>
  );
}

export function Bring({ n }: { n: NowContext }) {
  if (!n.bring.length) return <p className="subtle">Nothing special to bring.</p>;
  return <CheckList id={`bring:${n.nextUp.item?.id ?? n.item?.id}`} items={n.bring} />;
}

// ───────────────────────────── cooking ─────────────────────────────

interface KitchenTimer {
  id: string;
  label: string;
  endsAt: number;
  total: number;
  fired: boolean;
}

/** Recipe steps (from the routine's notes) + big kitchen timers + optional hands-free voice commands. */
export function Kitchen({ n }: { n: NowContext }) {
  const now = useNow(500);
  const steps = useMemo(() => notesToSteps(n.routine?.notes), [n.routine?.notes]);
  const [step, setStep] = useState(0);
  const [timers, setTimers] = useState<KitchenTimer[]>(() => store.get<KitchenTimer[]>('tempo.kitchenTimers', []).filter((t) => t.endsAt > Date.now() - 3600_000));
  const [custom, setCustom] = useState('');
  useWakeLock(true);

  useEffect(() => store.set('tempo.kitchenTimers', timers), [timers]);
  useEffect(() => {
    const due = timers.filter((t) => !t.fired && t.endsAt <= now);
    if (due.length) {
      alarm(5);
      setTimers((ts) => ts.map((t) => (due.some((d) => d.id === t.id) ? { ...t, fired: true } : t)));
    }
  }, [now, timers]);

  const add = (seconds: number, label?: string) => {
    unlockAudio();
    setTimers((ts) => [...ts, { id: `${Date.now()}-${Math.random()}`, label: label || `${seconds >= 60 ? `${Math.round(seconds / 60)} min` : `${seconds} s`} timer`, endsAt: Date.now() + seconds * 1000, total: seconds * 1000, fired: false }]);
  };
  const stepRef = useRef(steps.length);
  stepRef.current = steps.length;
  const voice = useSpeech({
    onFinal: (text) => {
      const c = parseKitchenCommand(text);
      if (!c) return;
      if (c.kind === 'timer') {
        add(c.seconds);
        toast(`Timer set – ${mmss(c.seconds * 1000)}`);
      } else if (c.kind === 'next') setStep((s) => Math.min(stepRef.current - 1, s + 1));
      else if (c.kind === 'back') setStep((s) => Math.max(0, s - 1));
      else setTimers((ts) => ts.filter((t) => !t.fired));
    },
  });

  return (
    <div className="stack">
      {steps.length > 0 && (
        <div className="recipe">
          <div className="a-label">
            Step {step + 1} of {steps.length}
          </div>
          <div className="recipe-step">{steps[step]}</div>
          <div className="row" style={{ gap: 8 }}>
            <Button icon onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} aria-label="Previous step">
              <ChevronLeft size={20} />
            </Button>
            <Button className="grow" onClick={() => setStep(Math.min(steps.length - 1, step + 1))} disabled={step >= steps.length - 1}>
              Next step <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      )}
      {timers.map((t) => {
        const left = t.endsAt - now;
        return (
          <div key={t.id} className={`ktimer ${t.fired ? 'fired' : ''}`} onClick={() => t.fired && setTimers((ts) => ts.filter((x) => x.id !== t.id))}>
            <div className="grow">
              <div className="small subtle">{t.label}</div>
              <div className="ktime">{t.fired ? 'Done!' : mmss(left)}</div>
            </div>
            {!t.fired && (
              <Button size="sm" onClick={() => setTimers((ts) => ts.map((x) => (x.id === t.id ? { ...x, endsAt: x.endsAt + 60_000, total: x.total + 60_000 } : x)))}>
                +1 min
              </Button>
            )}
            <Button size="sm" icon variant="ghost" onClick={() => setTimers((ts) => ts.filter((x) => x.id !== t.id))} aria-label="Remove timer">
              <X size={16} />
            </Button>
          </div>
        );
      })}
      <div className="chips">
        {[1, 3, 5, 10, 15, 20].map((m) => (
          <button key={m} className="chip big" onClick={() => add(m * 60)}>
            {m} min
          </button>
        ))}
      </div>
      <div className="row" style={{ gap: 8 }}>
        <input className="input" style={{ maxWidth: 120 }} inputMode="numeric" placeholder="mins" value={custom} onChange={(e) => setCustom(e.target.value.replace(/[^\d]/g, ''))} />
        <Button disabled={!custom} onClick={() => (add(Number(custom) * 60), setCustom(''))}>
          Start
        </Button>
        {voice.supported && (
          <Button variant={voice.listening ? 'primary' : undefined} onClick={() => (unlockAudio(), voice.listening ? voice.stop() : voice.start())}>
            {voice.listening ? <MicOff size={16} /> : <Mic size={16} />} {voice.listening ? 'Hands-free on' : 'Hands-free'}
          </Button>
        )}
      </div>
      {voice.listening && <p className="tiny faint">Say "ten minute timer", "next", "back" or "stop".</p>}
      {voice.error && <div className="callout danger small">{voice.error}</div>}
      {steps.length === 0 && <p className="tiny faint">Tip: put a recipe in this routine's notes (one step per line) to step through it here.</p>}
    </div>
  );
}

// ───────────────────────────── gym ─────────────────────────────

export function Workout({ n }: { n: NowContext }) {
  const now = useNow(500);
  const item = n.item!;
  const [rest, setRest] = useState<{ endsAt: number; total: number } | null>(null);
  const [sets, setSets] = useState(() => store.get<number>(`tempo.sets.${item.id}`, 0));
  const fired = useRef(false);
  useWakeLock(true);
  useEffect(() => store.set(`tempo.sets.${item.id}`, sets), [sets, item.id]);
  useEffect(() => {
    if (rest && !fired.current && now >= rest.endsAt) {
      fired.current = true;
      alarm(3);
    }
  }, [now, rest]);
  const startRest = (s: number) => {
    unlockAudio();
    fired.current = false;
    setRest({ endsAt: Date.now() + s * 1000, total: s * 1000 });
  };
  const total = item.end - item.start;
  const elapsed = Math.min(total, Math.max(0, now - item.start));
  const restLeft = rest ? rest.endsAt - now : 0;
  return (
    <div className="stack">
      <div className="progress" style={{ height: 8 }}>
        <div style={{ width: `${(elapsed / total) * 100}%` }} />
      </div>
      <div className="row subtle small" style={{ justifyContent: 'space-between' }}>
        <span>{mmss(elapsed)} in</span>
        <span>{duration(Math.max(0, item.end - now))} left</span>
      </div>
      <div className="row wrap" style={{ gap: 22, alignItems: 'center' }}>
        <Ring fraction={rest && restLeft > 0 ? restLeft / rest.total : 0} size={160}>
          <div className="ring-time">{rest && restLeft > 0 ? mmss(restLeft) : rest ? 'Go!' : 'Rest'}</div>
        </Ring>
        <div className="stack tight">
          <div className="chips">
            {[60, 90, 120, 180].map((s) => (
              <button key={s} className="chip big" onClick={() => startRest(s)}>
                {s >= 120 ? `${s / 60} min` : `${s}s`}
              </button>
            ))}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <Button icon onClick={() => setSets(Math.max(0, sets - 1))} aria-label="One set fewer">
              <Minus size={18} />
            </Button>
            <div className="a-big" style={{ minWidth: 90, textAlign: 'center' }}>
              {sets} set{sets === 1 ? '' : 's'}
            </div>
            <Button icon variant="primary" onClick={() => (setSets(sets + 1), startRest(90))} aria-label="Set done">
              <Plus size={18} />
            </Button>
            <Button icon variant="ghost" onClick={() => setSets(0)} aria-label="Reset sets">
              <RotateCcw size={16} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────── shift / generic ─────────────────────────────

export function Until({ n, now, note }: { n: NowContext; now: number; note?: string }) {
  const item = n.item!;
  return (
    <div className="stack tight">
      <div className="a-countdown">until {time(item.end)}</div>
      <div className="subtle">
        {duration(Math.max(0, item.end - now))} left{note ? ` · ${note}` : ''}
      </div>
    </div>
  );
}
